import React, { useEffect, useReducer, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { RFQ, RFQItem } from '../../../../types/rfq';
import { EmailAttachment } from '../../../../types/email';
import { readStoredExcelFresh } from '../../../../api/rfqFiles';
import { runExcelAutomationRequest, type ExcelPriceLine } from '../../../../services/excelAutomation';
import { toast } from '../../../../utils/notifications';
import { queryKeys } from '../../../../api/queryKeys';
import { listVerifiedExcelFiles, type ExcelFileInfo } from './excelFiles';
import {
  blobToBase64,
  buildPriceData,
  classifyAutomation,
  excelMimeTypeFor,
  findCachedRfq,
  readFailureToast,
  upsertAttachment,
  type AutomationFileResult,
} from './quoteExcelLogic';

interface UseQuoteExcelOptions {
  open: boolean;
  allItemsValidated: boolean;
  /** Run the automation once, unprompted, when the dialog opens. */
  autoRun: boolean;
  /** Who the auto-run is for, for the log line only. */
  autoRunFor: string;
  /** A Seven Seas portal RFQ: their Excel is required, so say so when it is missing. */
  sevenSeasPortal: boolean;
  setFileAttachments: React.Dispatch<React.SetStateAction<EmailAttachment[]>>;
}

/**
 * The automation run's lifecycle. `scheduled` is the open-time auto-run's
 * 500 ms wait: Send is blocked from the moment it is scheduled, not only once
 * it is running. `jobId` and `lastDuration` arrive with the backend's answer;
 * the job id is cleared again when the run ends.
 */
interface RunState {
  phase: 'idle' | 'scheduled' | 'running';
  startTime: number | null;
  jobId: string | null;
  lastDuration: number | null;
}

type RunEvent =
  | { type: 'schedule' }
  | { type: 'timerFired' }
  | { type: 'start'; startTime: number }
  | { type: 'answered'; jobId: string | null; duration: number }
  | { type: 'end' };

const INITIAL_RUN: RunState = { phase: 'idle', startTime: null, jobId: null, lastDuration: null };

function runReducer(state: RunState, event: RunEvent): RunState {
  switch (event.type) {
    case 'schedule':
      return { ...state, phase: 'scheduled' };
    case 'timerFired':
      // The run it scheduled starts in the same tick, or not at all.
      return state.phase === 'scheduled' ? { ...state, phase: 'idle' } : state;
    case 'start':
      return { ...state, phase: 'running', startTime: event.startTime };
    case 'answered':
      return { ...state, jobId: event.jobId, lastDuration: event.duration };
    case 'end':
      return { ...state, phase: 'idle', startTime: null, jobId: null };
  }
}

/** The toast after listing the stored Excel files: how many, missing, repaired. */
function reportListing(files: ExcelFileInfo[], missingFiles: string[], fixedCount: number) {
  if (!(files.length > 0 && files[0].source === 'database')) return;
  const missingCount = missingFiles.length;

  let description = `Found ${files.length} Excel file(s)`;
  if (missingCount > 0) {
    description += ` (${missingCount} missing from storage)`;
  }
  if (fixedCount > 0) {
    description += ` (${fixedCount} file sizes auto-fixed)`;
  }

  toast({
    title: "Excel Files Loaded",
    description,
    severity: missingCount > 0 ? "warning" : "success"
  });

  if (missingCount > 0) {
    console.warn('Missing files:', missingFiles);
  }
}

/** Save a stored Excel to the user's machine, read back fresh. */
async function downloadStoredExcel(rfqId: number, filename: string) {
  try {
    const read = await readStoredExcelFresh(rfqId, filename);

    if ('failure' in read) {
      const { title, description } = readFailureToast(read.failure, "Download Failed");
      toast({ title, description, severity: "error" });
      return;
    }

    // Create download link
    const url = window.URL.createObjectURL(read.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    toast({
      title: "Download Started",
      description: `Downloading ${filename}`,
      severity: "success"
    });

  } catch (error) {
    console.error('Error downloading Excel file:', error);
    toast({
      title: "Error",
      description: "Failed to download Excel file - check network connection",
      severity: "error"
    });
  }
}

/** Toasts for a run whose files came back, after the written ones are re-attached. */
function reportAttachments(
  outcome: ReturnType<typeof classifyAutomation>,
  priceCount: number,
  attachedCount: number,
  failedAttachments: string[],
) {
  if (attachedCount > 0) {
    toast({
      title: "Files Auto-Attached",
      description: `${attachedCount} updated Excel file(s) have been automatically attached to the email`,
      severity: "info"
    });
  }

  const attachedPartials = outcome.partial.filter(
    (r) => !failedAttachments.includes(r.filename),
  );
  if (attachedPartials.length > 0) {
    toast({
      title: "Some Prices Not Written",
      description: `Only ${attachedPartials.map((r) => `${r.updatedCount} of ${priceCount} price(s) were written into ${r.filename}`).join(', ')}. Check the sheet and fill in the missing prices before sending.`,
      severity: "warning"
    });
  }

  const refusedFiles = outcome.refused.map((r) => r.filename);
  const failedToAttach = failedAttachments.filter(filename => !refusedFiles.includes(filename));
  if (failedToAttach.length > 0) {
    toast({
      title: "Attach Failed",
      description: `Could not attach the updated ${failedToAttach.join(', ')}. The stale copy was removed - re-run the update before sending, since the copy in storage has no prices in it.`,
      severity: "error"
    });
  }

  if (outcome.unfilled.length > 0) {
    toast({
      title: "Excel Not Filled",
      description: `No prices were written into ${outcome.unfilled.map((r) => r.filename).join(', ')} - the item rows could not be found. It has not been attached; fill in the prices by hand before sending.`,
      severity: "error"
    });
  }
}

/**
 * The customer's own Excel: listing the stored copies, filling them with the
 * validated prices (the automation), reading them back fresh, and attaching
 * them to the quote email.
 */
export function useQuoteExcel(rfq: RFQ, {
  open,
  allItemsValidated,
  autoRun,
  autoRunFor,
  sevenSeasPortal,
  setFileAttachments,
}: UseQuoteExcelOptions) {
  const queryClient = useQueryClient();
  const [excelFiles, setExcelFiles] = useState<ExcelFileInfo[]>([]);
  const [isLoadingExcelFiles, setIsLoadingExcelFiles] = useState(false);
  const [run, dispatch] = useReducer(runReducer, INITIAL_RUN);
  const [updatedFiles, setUpdatedFiles] = useState<Set<string>>(new Set()); // Track which files have been updated
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const isRunningAutomation = run.phase === 'running';

  // Automatically run Excel automation (wrist.com, Seven Seas) when the dialog opens
  const hasRunAutoExcelAutomation = useRef(false);

  useEffect(() => {
    const shouldAutoRun = open &&
                          autoRun &&
                          allItemsValidated &&
                          !isRunningAutomation &&
                          !hasRunAutoExcelAutomation.current &&
                          rfq.rfq_id;

    if (shouldAutoRun) {
      console.info(`Auto-running Excel automation for ${autoRunFor} email: ${rfq.client_email}`);
      hasRunAutoExcelAutomation.current = true;
      dispatch({ type: 'schedule' });

      // Run Excel automation after a short delay to ensure UI is ready
      setTimeout(async () => {
        try {
          // Batched with runExcelAutomation's own 'start', so there is no
          // render in which the run is neither scheduled nor running.
          dispatch({ type: 'timerFired' });
          await runExcelAutomation();
        } catch (error) {
          console.error('Auto Excel automation failed:', error);
        }
      }, 500);
    }

    // Reset the flag when dialog closes
    if (!open) {
      hasRunAutoExcelAutomation.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rfq.client_email, rfq.rfq_id, allItemsValidated, isRunningAutomation, autoRun, autoRunFor]);

  const loadExcelFiles = async () => {
    if (!rfq.rfq_id) return;

    setIsLoadingExcelFiles(true);
    try {
      const listing = await listVerifiedExcelFiles(rfq.rfq_id);

      if ('error' in listing) {
        console.error('Supabase error loading Excel files:', listing.error);
        toast({
          title: "Error",
          description: "Failed to load Excel files from database",
          severity: "error"
        });
        return;
      }

      const { files, missingFiles, fixedSizes } = listing;
      setExcelFiles(files);

      reportListing(files, missingFiles, fixedSizes.length);

    } catch (error) {
      console.error('Error loading Excel files:', error);
      toast({
        title: "Error",
        description: "Failed to load Excel files",
        severity: "error"
      });
    } finally {
      setIsLoadingExcelFiles(false);
    }
  };

  /** Attach what the backend wrote; drop every copy that must not be sent. */
  const attachAutomationResults = async (results: AutomationFileResult[], priceData: ExcelPriceLine[]) => {
    const outcome = classifyAutomation(results, priceData.length);
    let attachedCount = 0;
    // Files the backend reported as failed start out in the failed set:
    // storage still holds their unpriced copy, so attaching one would
    // email exactly the empty sheet this whole path exists to prevent.
    const failedAttachments: string[] = results
      .filter((r) => r.success !== true)
      .map((r) => r.filename)
      .filter((filename) => Boolean(filename));
    // An unfilled file is not attached either, and an earlier copy of it
    // is taken off - but it gets its own error toast.
    const unfilledNames = outcome.unfilled
      .map((r) => r.filename)
      .filter((filename) => Boolean(filename));
    if (unfilledNames.length > 0) {
      setFileAttachments(prev => prev.filter(a => !unfilledNames.includes(a.name)));
    }

    for (const written of outcome.written) {
      const filename = written.filename;
      try {
        // No per-file toast; a collective one follows
        const wasAttached = await attachExcelFile(
          filename,
          false,
          written.contentBase64,
          written.contentType
        );
        if (wasAttached) {
          attachedCount++;
        } else {
          failedAttachments.push(filename);
        }
      } catch (error) {
        console.error(`Failed to auto-attach ${filename}:`, error);
        failedAttachments.push(filename);
      }
    }

    // A file we could not re-attach must not stay attached from before
    // the automation ran - that copy has no prices in it. Removing it
    // makes a missing attachment obvious instead of sending an empty one.
    if (failedAttachments.length > 0) {
      setFileAttachments(prev => prev.filter(a => !failedAttachments.includes(a.name)));
    }
    reportAttachments(outcome, priceData.length, attachedCount, failedAttachments);
  };

  /** The backend answered 2xx: record, report and attach what it wrote. */
  const handleAutomationAnswer = async (body: { data?: Record<string, unknown> } | null, priceData: ExcelPriceLine[], duration: number) => {
    const data = (body?.data || {}) as { jobId?: string; results?: AutomationFileResult[]; validatedItemsCount?: number };
    dispatch({ type: 'answered', jobId: data.jobId ?? null, duration });

    const automationResults: AutomationFileResult[] = data.results || [];
    const outcome = classifyAutomation(automationResults, priceData.length);
    const processedFiles = outcome.written.map((r) => r.filename);
    setUpdatedFiles(prev => {
      const newSet = new Set(prev);
      processedFiles.forEach(filename => newSet.add(filename));
      return newSet;
    });

    // A workbook the backend refused to fill by position is still the
    // customer's unpriced sheet; say why, since re-running will not help.
    if (outcome.refused.length > 0) {
      toast({
        title: "Excel Not Auto-Filled",
        description: outcome.refused.map((r) => `${r.filename}: ${r.error}`).join(' '),
        severity: "warning"
      });
    }

    if (outcome.written.length > 0) {
      toast({
        title: "Excel Files Updated",
        description: outcome.countKnown
          ? `Wrote ${outcome.writtenSummary} in ${(duration / 1000).toFixed(1)}s`
          : `Processed ${outcome.written.length} Excel file(s) with ${data.validatedItemsCount || 0} validated prices in ${(duration / 1000).toFixed(1)}s`,
        severity: "success"
      });
    }

    // Automatically attach the updated Excel files immediately after automation
    if (automationResults.length > 0) {
      await attachAutomationResults(automationResults, priceData);
    }

    // Refresh file list to show updated files
    setTimeout(() => loadExcelFiles(), 2000);
  };

  const runExcelAutomation = async () => {
    if (!rfq.rfq_id || !allItemsValidated) return;

    const startTime = Date.now();
    dispatch({ type: 'start', startTime });

    try {
      // Wait a moment to ensure any pending mutations complete and cache is updated
      // This prevents race conditions where users click Excel Automation immediately after adding remarks
      await new Promise(resolve => setTimeout(resolve, 300));

      // Refetch to ensure we have the latest data from cache/database
      await queryClient.refetchQueries({ queryKey: queryKeys.rfqs.all });

      // The refreshed RFQ, with the latest remarks
      const refreshedRfq = findCachedRfq(queryClient, rfq.rfq_id);
      const itemsToUse: RFQItem[] = refreshedRfq?.items || rfq.items || [];
      const priceData = buildPriceData(itemsToUse);

      if (priceData.length === 0) {
        toast({
          title: "No Valid Prices",
          description: "No validated prices found to update in Excel files",
          severity: "warning"
        });
        return;
      }

      const response = await runExcelAutomationRequest(rfq.rfq_id, priceData);

      if (response.ok) {
        const result = await response.json();
        await handleAutomationAnswer(result, priceData, Date.now() - startTime);
      } else {
        const error = await response.json().catch(() => ({}));
        // An RFQ with no Excel attached has nothing to fill - say so once
        // instead of failing the auto-run.
        if (response.status === 404 && /no excel files/i.test(error.message || '')) {
          toast({
            title: "No Excel File",
            description: sevenSeasPortal
              ? "This RFQ has no Excel attached. Seven Seas expects their Excel file - attach it filled in before sending."
              : "This RFQ has no Excel attached, so there is nothing to fill.",
            severity: "warning"
          });
          return;
        }
        throw new Error(error.message || "Failed to run Excel automation");
      }
    } catch (error) {
      console.error('Error running Excel automation:', error);
      toast({
        title: "Excel Update Failed",
        description: error instanceof Error && error.message ? error.message : "Failed to run Excel automation",
        severity: "error"
      });
      throw error;
    } finally {
      dispatch({ type: 'end' }); // also clears the job id, hiding the "job is running" message
    }
  };

  const downloadExcelFile = (filename: string) => downloadStoredExcel(rfq.rfq_id, filename);

  /**
   * Attach the quoted Excel to the outgoing email.
   *
   * `prefetchedBase64` is the copy the automation endpoint just wrote and
   * returned. Prefer it: the storage object is overwritten in place at a
   * stable URL, so re-downloading can hand back the pre-quote (empty) sheet
   * from a cache and silently email that to the customer.
   *
   * Without it - the manual Attach button - the object still has to be read
   * back, so that read goes through `readStoredExcelFresh`. A plain download
   * of the stable path is what handed out the stale copy 16 minutes after the
   * overwrite.
   */
  const attachExcelFile = async (
    filename: string,
    showToast: boolean = true,
    prefetchedBase64?: string,
    prefetchedContentType?: string
  ): Promise<boolean> => {
    try {
      if (prefetchedBase64) {
        const prefetched: EmailAttachment = {
          name: filename,
          contentType: prefetchedContentType || excelMimeTypeFor(filename),
          content: prefetchedBase64
        };
        setFileAttachments(prev => upsertAttachment(prev, prefetched));
        return true;
      }

      const read = await readStoredExcelFresh(rfq.rfq_id, filename);

      if ('failure' in read) {
        if (showToast) {
          const { title, description } = readFailureToast(read.failure, "Attach Failed");
          toast({ title, description, severity: "error" });
        }
        return false;
      }

      const newAttachment: EmailAttachment = {
        name: filename,
        contentType: excelMimeTypeFor(filename),
        content: await blobToBase64(read.blob)
      };

      // Replace existing attachment if already present, otherwise add new.
      // The outcome is decided here, not inside the updater: React may defer
      // the updater, so a flag set from within it reads back as "not attached"
      // on a read-back that in fact succeeded.
      setFileAttachments(prev => upsertAttachment(prev, newAttachment));

      if (showToast) {
        toast({
          title: "File Attached",
          description: `${filename} has been attached to the email`,
          severity: "success"
        });
      }

      return true;

    } catch (error) {
      console.error('Error attaching Excel file:', error);
      if (showToast) {
        toast({
          title: "Error",
          description: "Failed to attach Excel file - check network connection",
          severity: "error"
        });
      }
      return false;
    }
  };

  // Load Excel files when dialog opens
  useEffect(() => {
    if (open && rfq.rfq_id) {
      loadExcelFiles();
    }
  }, [open, rfq.rfq_id]);

  // Real-time timer effect for automation progress
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (run.startTime) {
      setElapsedTime(0); // Reset elapsed time when automation starts
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [run.startTime]);

  return {
    excelFiles,
    isLoadingExcelFiles,
    isRunningAutomation,
    /** Running, or the open-time auto-run is scheduled: the Excel is not final. */
    isBusy: run.phase !== 'idle',
    automationJobId: run.jobId,
    updatedFiles,
    automationStartTime: run.startTime,
    lastAutomationDuration: run.lastDuration,
    elapsedTime,
    loadExcelFiles,
    runExcelAutomation,
    downloadExcelFile,
    attachExcelFile,
  };
}

export type QuoteExcel = ReturnType<typeof useQuoteExcel>;
